/* Cart bundle bar — sync from the buy-box section + global config bridge.
 *
 * Two jobs:
 *
 * 1. Bundle-bar labels: whenever the buy-box section is on the page, overwrite
 *    the cart drawer's progress-bar fallback labels with the buy-box's
 *    customizer values.
 *
 * 2. Global config bridge: the cart re-tier scripts (peachy-cart-bundle-lock.js
 *    and -flavor-upsell.js) need the bundle config (selling plans, tier names,
 *    caps, gift unlocks). That config only lives in the buy-box DOM. To let the
 *    cart be edited on ANY page — not just buy-box pages — we persist the config
 *    to localStorage whenever a buy box renders (the customer always builds
 *    their bundle on a buy-box page first), and expose it via
 *    window.PeachyBundleStore.load(). On pages WITHOUT a buy box, we also run a
 *    gift-reconcile fallback so editing the cart can never leave a gift the
 *    customer no longer qualifies for (which Shopify's discount would NOT make
 *    free — i.e. they'd be charged for it). The fallback stands down only on
 *    pages whose buy box owns gifts (subscription mode, gift config present);
 *    one-time buy-box pages rely on this fallback too.
 */
(function () {
  'use strict';

  var SEP = '|';
  var CFG_KEY = 'peachy:bundleCfg';
  var SRC_GIFT = 'peachy-buy-box';

  // Only a subscription-mode buy box emits a gift config and runs its own
  // gift reconciliation. A one-time buy box renders [data-bundle] but no
  // gift config (Liquid-gated), so gift ownership falls to this script —
  // otherwise a subscription re-tier done from the one-time page (via
  // peachy-cart-bundle-lock) never gets its newly-earned gift added.
  function buyBoxOwnsGifts() {
    return !!document.querySelector('script[data-peachy-gift-config]');
  }

  function readConfig() {
    var el = document.querySelector('[data-bundle]:not([data-bundle-arm])');
    if (!el) return null;

    var prices = (el.dataset.tierPrices || '').split(SEP);
    var discounts = (el.dataset.tierDiscounts || '').split(SEP);
    var badges = (el.dataset.tierBadges || '').split(SEP);
    if (prices.length < 4) return null;

    return {
      prices: prices,
      discounts: discounts,
      badges: badges,
      shippingLocked: el.dataset.shippingLocked || '',
      shippingUnlocked: el.dataset.shippingUnlocked || '',
      shippingThreshold: parseInt(el.dataset.shippingThreshold, 10) || 2
    };
  }

  // Shipping A/B test belt for the drawer's "Shipping: FREE" row. The
  // server render is arm-aware via the cart attribute, but two races
  // remain: a render can precede the attribute write (row wrongly
  // visible for the paid arm), and a stale 'paid-1pack' attribute can
  // hide the row for a visitor who no longer carries the class. This
  // runs on drawer mutations, on cart-data arrival, and on late class
  // arrival. It does NOTHING while the cart is still unknown, and only
  // un-hides what it hid itself (marker) or what a stale attribute hid
  // (class absent + subscription bags present).
  function applyShipRowBelt() {
    var shipRow = document.querySelector('.shipping-price');
    if (!shipRow || lastKnownItems === null) return;
    var bags = countSubscriptionBags(lastKnownItems);
    if (document.documentElement.classList.contains('peachy-ship-v2')) {
      if (bags < 2) {
        if (!shipRow.classList.contains('hidden')) {
          shipRow.classList.add('hidden');
          shipRow.dataset.peachyShipBelt = '1';
        }
      } else if (shipRow.dataset.peachyShipBelt === '1') {
        shipRow.classList.remove('hidden');
        delete shipRow.dataset.peachyShipBelt;
      }
    } else if (bags >= 1 && shipRow.classList.contains('hidden')) {
      // Class-less visitor with subscription bags: the server only
      // hides this row for them when a stale paid-arm attribute is on
      // the cart — safe to restore.
      shipRow.classList.remove('hidden');
    }
  }

  function applyConfig() {
    applyShipRowBelt();

    var config = readConfig();
    if (!config) return;

    document.querySelectorAll('.peachy-cart-bundle').forEach(function (bar) {
      // Per-tier labels.
      var steps = bar.querySelectorAll('.peachy-cart-bundle__step');
      steps.forEach(function (step) {
        var i = parseInt(step.getAttribute('data-step'), 10);
        if (!i || i < 1 || i > 4) return;

        var price = config.prices[i - 1];
        var discount = config.discounts[i - 1];
        var badge = config.badges[i - 1];

        var priceEl = step.querySelector('.peachy-cart-bundle__price');
        // Per-unit suffix comes from Liquid (window.peachyStrings, set in
        // snippets/cart-drawer.liquid) so it follows the storefront
        // language; '/ea' is the English fallback.
        var perEach = (window.peachyStrings && window.peachyStrings.perEach) || '/ea';
        if (priceEl && price && priceEl.textContent !== price + perEach) {
          priceEl.textContent = price + perEach;
        }

        var discountEl = step.querySelector('.peachy-cart-bundle__discount');
        if (discountEl) {
          if (discount) {
            if (discountEl.textContent !== discount) discountEl.textContent = discount;
            discountEl.hidden = false;
          } else {
            discountEl.textContent = '';
            discountEl.hidden = true;
          }
        }

        var badgeEl = step.querySelector('.peachy-cart-bundle__badge');
        if (badgeEl) {
          if (badge) {
            // Write only on change: an unconditional set is a childList
            // mutation, and this runs FROM the drawer's own observer —
            // a self-sustaining per-frame loop that also starved the
            // CLEAN10 debounce below (review 2026-09-09).
            if (badgeEl.textContent !== badge) badgeEl.textContent = badge;
            badgeEl.hidden = false;
          } else {
            badgeEl.remove();
          }
        }
      });

      // Shipping copy. Only override the UNLOCKED variant; the locked
      // variant is left to Liquid (snippets/peachy-cart-bundle-bar.liquid),
      // which uses cart.item_count server-side and renders proper plural
      // ("bag" vs "bags"). Earlier iterations overrode the locked text
      // here using the cart-icon-bubble for the count, but the bubble
      // updates on a different timing than the drawer, so the count
      // would lag and produce wrong messages like "Add 2 more bags"
      // immediately after adding the first bag.
      var shipping = bar.querySelector('.peachy-cart-bundle__shipping');
      var shippingTextEl = shipping && shipping.querySelector('span');
      if (shipping && shippingTextEl && shipping.classList.contains('is-unlocked')) {
        if (config.shippingUnlocked) shippingTextEl.textContent = config.shippingUnlocked;
      }
    });
  }

  // ---- Global config bridge -------------------------------------------------

  // Persist the full bundle config to localStorage when a buy box is present,
  // so the cart can re-tier + reconcile on pages without a buy box.
  function persistConfig() {
    var el = document.querySelector('[data-bundle]:not([data-bundle-arm])');
    if (!el) return;
    try {
      var gifts = [];
      var giftEl = document.querySelector('script[data-peachy-gift-config]');
      if (giftEl) {
        var parsed = JSON.parse(giftEl.textContent.trim());
        if (parsed && Array.isArray(parsed.gifts)) {
          gifts = parsed.gifts
            .filter(function (g) { return g && g.variantId && g.unlockAt; })
            .map(function (g) { return { variantId: g.variantId, unlockAt: g.unlockAt, label: g.label || '' }; });
        }
      } else {
        // No gift config on this page (one-time buy box) — keep the gift
        // list from the last subscription buy-box visit instead of wiping
        // it, or the reconcile fallback below has nothing to re-add.
        var prior = loadStoredConfig();
        if (prior && Array.isArray(prior.gifts)) gifts = prior.gifts;
      }
      var maxQty = parseInt(el.dataset.maxQty, 10) || 4;
      var hardCap = parseInt(el.dataset.hardCap, 10) || maxQty;
      if (hardCap < maxQty) hardCap = maxQty;
      var cfg = {
        sellingPlans: (el.dataset.sellingPlans || '').split(','),
        bundleNames: (el.dataset.bundleNames || '').split(SEP),
        maxQty: maxQty,
        hardCap: hardCap,
        gifts: gifts,
        // Market stamp (see the __peachyMarket bridge in the drawer
        // snippet): caps and the gift list are market-specific, so a
        // config persisted under one country must not drive re-tiers
        // after a mid-session switch to the other.
        country: (window.__peachyMarket && window.__peachyMarket.country) || ''
      };
      localStorage.setItem(CFG_KEY, JSON.stringify(cfg));
    } catch (e) { /* localStorage blocked / malformed — ignore */ }
  }

  function loadStoredConfig() {
    try {
      var raw = localStorage.getItem(CFG_KEY);
      if (!raw) return null;
      var cfg = JSON.parse(raw);
      if (!cfg || !cfg.sellingPlans || cfg.sellingPlans.length < 4) return null;
      // Mid-session market switch: the stored config was persisted under
      // the other country. Selling plans/bundle names are shared across
      // markets (percentage plans, same ids) and stay usable, but the
      // gift list is dropped (the other market may offer gifts this one
      // doesn't sell — reconcile falls back to sweeps-only) and the caps
      // clamp to this market's pouch limit so drawer re-tiers can't
      // exceed it. The stamp itself only refreshes on the next buy-box
      // visit, which persists a genuine config for this market.
      var mkt = window.__peachyMarket || {};
      if (mkt.country && cfg.country !== mkt.country) {
        if (mkt.maxBags > 0) {
          // Into a capped market — including UNSTAMPED legacy configs
          // (persisted before the stamp existed): clamp and drop the
          // foreign gift list.
          cfg.gifts = [];
          if (!cfg.maxQty || cfg.maxQty > mkt.maxBags) cfg.maxQty = mkt.maxBags;
          if (!cfg.hardCap || cfg.hardCap > mkt.maxBags) cfg.hardCap = mkt.maxBags;
        } else if (cfg.country) {
          // Stamped foreign config in an uncapped market (CA→US after a
          // footer switch): drop the foreign gifts and lift the caps —
          // but never let maxQty exceed the plans the config actually
          // carries (the CA persist blanks the 4th slot; indexing into
          // an empty plan would strip the subscription on a re-tier).
          cfg.gifts = [];
          var planCount = (cfg.sellingPlans || []).filter(Boolean).length || 1;
          cfg.maxQty = Math.min(4, planCount);
          cfg.hardCap = 10;
        }
        // Unstamped config in an uncapped market = legacy US-in-US: as-is.
      }
      return cfg;
    } catch (e) { return null; }
  }

  window.PeachyBundleStore = { load: loadStoredConfig };

  // ---- Gift reconcile fallback (non-buy-box pages only) ---------------------


  // Last cart state we've heard about (from cartUpdate events or our own
  // fetches). Powers the zero-latency checkout guard below: prod order
  // #271233 proved a customer can delete the paid bags and click Checkout
  // INSIDE the async window before any reconcile sweep lands, checking out
  // a stranded "free" bag that the discount function rightly won't zero —
  // so they get charged for a line labeled FREE, on the bundle plan.
  var lastKnownItems = null;

  // Delegated to the shared gift engine (peachy-gift-engine.js loads
  // first in the drawer snippet's defer chain). The inline copies below
  // are EMERGENCY fallbacks only — the checkout guard is safety-critical
  // and must not fail open if the engine asset ever 404s after a partial
  // push (2026-08-19 review round 2). Keep them in sync with the engine.
  var warnedNoEngine = false;
  function noteNoEngine() {
    if (warnedNoEngine) return;
    warnedNoEngine = true;
    if (window.console && console.error) {
      console.error('[peachy-cart-bundle-sync] PeachyGiftEngine missing — running emergency local sweeps');
    }
  }
  function overTierGiftKeys(items) {
    if (window.PeachyGiftEngine) return window.PeachyGiftEngine.overTierGiftKeys(items);
    noteNoEngine();
    var bags = countSubscriptionBags(items);
    var familyPaidUnits = {};
    (items || []).forEach(function (it) {
      var bid = it.properties && it.properties._bundleId;
      if (!bid) return;
      if (it.properties._free_gift) return;
      familyPaidUnits[bid] = (familyPaidUnits[bid] || 0) + it.quantity;
    });
    return (items || [])
      .filter(function (it) {
        if (!(it.properties && it.properties._free_gift)) return false;
        var tier = parseInt(it.properties._gift_tier || '0', 10);
        if (!tier || tier > bags) return true;
        var bid = it.properties._bundleId;
        if (bid && (familyPaidUnits[bid] || 0) < 2) return true;
        return false;
      })
      .map(function (it) { return it.key; });
  }

  // The Canada import cap limits POUCHES per shipment, not subscriptions:
  // count every non-gift line that is either on a selling plan or is a
  // Peachy Clean pouch product (one-time pouches ship too).
  function countCapPouches(items) {
    return (items || []).reduce(function (sum, it) {
      if (it.properties && it.properties._free_gift) return sum;
      if (it.selling_plan_allocation) return sum + it.quantity;
      if ((it.handle || '').indexOf('peachy-clean') !== -1) return sum + it.quantity;
      return sum;
    }, 0);
  }

  function countSubscriptionBags(items) {
    if (window.PeachyGiftEngine) return window.PeachyGiftEngine.countFunctionBags(items);
    noteNoEngine();
    return (items || []).reduce(function (sum, it) {
      if (it.properties && it.properties._free_gift) return sum;
      return it.selling_plan_allocation ? sum + it.quantity : sum;
    }, 0);
  }

  // ---- Shipping A/B test: arm attribute writer ----
  // The delivery-customization function (peachy-discount-functions →
  // peachy-shipping-test) reads the visitor's arm from the cart
  // attribute _peachyShipTest: 'paid-1pack' when IntelliGems set
  // html.peachy-ship-v2, else 'free'. Inert until that function is
  // activated. Written only once a cart is known to exist (never
  // creates one), re-synced on every cart event, and tolerant of the
  // class arriving late (async IG SDK) via the observer below.
  // QA toggle: ?ship=v2 simulates the IntelliGems variant class, same
  // as ?offer=v2 did for the offer test. Added here (deferred script)
  // so it exercises the late-arrival path the real injection uses.
  if (/[?&]ship=v2\b/.test(window.location.search)) {
    document.documentElement.classList.add('peachy-ship-v2');
  }

  var shipArmWritten = null;
  var shipArmCartSeen = false;
  function syncShipArm() {
    if (!shipArmCartSeen) return;
    // Shipping policy note (2026-09-09): subscription-gated free
    // shipping went live via the peachy-sub-shipping discount Function,
    // which ignores this attribute entirely — the stamp exists only for
    // the HELD paid-1pack shipping A/B test and stays at the held
    // test's control value.
    var arm = document.documentElement.classList.contains('peachy-ship-v2')
      ? 'paid-1pack'
      : 'free';
    if (arm === shipArmWritten) return;
    shipArmWritten = arm; // optimistic — reset on failure so we retry
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { _peachyShipTest: arm } })
    })
      .then(function (r) {
        // A resolved-but-failed response (429, 4xx) must retry too,
        // not just network rejections.
        if (!r.ok) shipArmWritten = null;
      })
      .catch(function () { shipArmWritten = null; });
  }
  // Register a cart we've actually SEEN. Empty carts are never
  // annotated (Shopify hands every visitor a token, so a token is not
  // evidence a cart exists — writing would mint one per pageview).
  // Seeding shipArmWritten from the cart's existing attribute avoids a
  // redundant POST on every page load for returning visitors.
  function noteCartForShipArm(cart) {
    if (!cart || !cart.items || !cart.items.length) return;
    shipArmCartSeen = true;
    if (
      shipArmWritten === null &&
      cart.attributes &&
      typeof cart.attributes._peachyShipTest === 'string' &&
      cart.attributes._peachyShipTest
    ) {
      shipArmWritten = cart.attributes._peachyShipTest;
    }
    syncShipArm();
    applyShipRowBelt();
  }
  new MutationObserver(function () {
    syncShipArm();
    applyShipRowBelt();
    // Late arm arrival can change gift entitlement (the variant's tin
    // unlocks at 1+ bag) — re-run the fallback so carts built before
    // assignment heal on non-buy-box pages too. Self-guarded: stands
    // down on buy-box pages and while a pass is already running.
    giftReconcileFallback();
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });

  function giftReconcileFallback() {
    // Stand down when a gift-owning buy box runs on this page (it
    // registers itself with the engine; the DOM check is the belt for
    // the inline-script parse window). No local in-flight flag: the
    // engine's page-wide lock QUEUES concurrent requests (pending flag)
    // instead of dropping them — a local flag in front of it silently
    // lost mid-pass events (2026-08-19 review round 2).
    if (buyBoxOwnsGifts()) return;
    if (!window.PeachyGiftEngine) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastKnownItems = (cart && cart.items) || [];
        noteCartForShipArm(cart);
        // The engine holds the pass logic AND the page-wide lock; it
        // also no-ops here whenever a buy box has claimed the page.
        return window.PeachyGiftEngine.reconcileFallback(cart);
      })
      .catch(function () { /* leave cart as-is; server function still backstops discounting */ });
  }

  // ——— CLEAN10 cart attach (Buy Box V3 plan, 2026-09-09) ———
  // The code is a component of the SUBSCRIPTION price design, so the
  // cart itself — not just checkout — must carry it whenever a
  // subscription line is present: the drawer's cart_code_pct Liquid
  // then shows code-adjusted per-line prices. A cart-applied
  // percentage code lands in cart_level_discount_applications and
  // leaves line final prices untouched (verified 2026-09-09), so that
  // Liquid never double-counts. Auto-attachment is remembered in
  // sessionStorage so a shopper-typed CLEAN10 on a one-time-only cart
  // is never stripped by us.
  var CLEAN10 = 'CLEAN10';

  function clearDiscountCookie() {
    var exp = '; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'discount_code=' + exp;
    document.cookie = 'discount_code=' + exp + '; domain=' + location.hostname;
    var parts = location.hostname.split('.');
    if (parts.length > 2) {
      document.cookie = 'discount_code=' + exp + '; domain=.' + parts.slice(-2).join('.');
    } else if (parts.length === 2) {
      document.cookie = 'discount_code=' + exp + '; domain=.' + location.hostname;
    }
  }
  var clean10Timer = null;
  var clean10InFlight = false;
  var clean10Pending = false;

  var clean10Deadline = 0;
  function scheduleClean10Reconcile() {
    var now = Date.now();
    // Debounce, but never past the deadline: a mutation storm used to
    // reset this timer every frame and the pass never ran at all.
    if (!clean10Deadline) clean10Deadline = now + 600;
    window.clearTimeout(clean10Timer);
    clean10Timer = window.setTimeout(function () {
      clean10Deadline = 0;
      reconcileClean10();
    }, Math.max(0, Math.min(120, clean10Deadline - now)));
  }

  // Auto-attach marker lives on the CART (attribute), not in
  // sessionStorage: the cart cookie survives days and sessions, so a
  // session-scoped marker let a subscription-only code ride along on a
  // later one-time-only cart (review 2026-09-09). Written on the same
  // request that applies the code, so it costs nothing extra.
  var CLEAN10_ATTR = '_peachyClean10Auto';
  function clean10WasAuto(cart) {
    return !!(cart && cart.attributes && cart.attributes[CLEAN10_ATTR]);
  }

  // ——— Pre-add write (ATC speed, 2026-09-28) ———
  // Phone timing showed a first subscription add paying for TWO drawer
  // renders (the add's, then the render gate's code write, ~1s each)
  // plus a separate 2.6s ship-arm attribute write racing the code
  // write. The buy boxes now route their pre-add write (the line
  // clear, or nothing at all) through here, and it carries the code,
  // the auto marker and the ship-arm stamp. No sections: the ADD that
  // follows renders the drawer once, with the code already on the
  // cart. Called only for Peachy Clean SUBSCRIPTION adds. Best-effort:
  // if the code doesn't land, the render gate above still applies it
  // after the add, exactly as before.
  //
  // While a buy-box add is between this write and its add, the cart can
  // briefly hold the auto code with no subscription line yet, which is
  // exactly what reconcile's strip branch looks for. The hold keeps
  // reconcile from stripping mid-add; release() ends it (the buy box
  // calls it once the add lands or fails), and it expires on its own.
  var preAddHoldUntil = 0;
  function currentShipArm() {
    return document.documentElement.classList.contains('peachy-ship-v2')
      ? 'paid-1pack'
      : 'free';
  }
  function preAddWrite(cart, updates) {
    cart = cart || {};
    var body = {};
    if (updates && Object.keys(updates).length) body.updates = updates;
    var attrs = {};

    if (!clean10Hopeless) {
      // Same code decisions as reconcileClean10's subscription branches:
      // keep every other code, add CLEAN10.
      var codes = ((cart.discount_codes) || []).map(function (d) {
        return typeof d === 'string' ? d : (d && d.code) || '';
      }).filter(Boolean);
      var hasCode = codes.some(function (c) { return c.toUpperCase() === CLEAN10; });
      if (!hasCode) body.discount = codes.concat(CLEAN10).join(',');
      if (!hasCode || !clean10WasAuto(cart)) attrs[CLEAN10_ATTR] = '1';
    }

    var arm = currentShipArm();
    var armOnCart = cart.attributes && cart.attributes._peachyShipTest;
    if (arm !== (shipArmWritten || armOnCart)) attrs._peachyShipTest = arm;
    if (Object.keys(attrs).length) body.attributes = attrs;

    if (!body.updates && body.discount == null && !body.attributes) {
      return Promise.resolve(null);
    }
    preAddHoldUntil = Date.now() + 20000;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (r.ok && body.attributes && body.attributes._peachyShipTest) {
        shipArmWritten = body.attributes._peachyShipTest;
        shipArmCartSeen = true;
      }
      return r;
    });
  }
  window.PeachyPreAdd = {
    write: preAddWrite,
    release: function () {
      preAddHoldUntil = 0;
      scheduleClean10Reconcile();
    }
  };



  // ——— Ghost-click shield after an add-to-cart ———
  // The v3 flavor slide-out closes the moment the CTA is tapped, and the
  // guarantee block sitting just below the button carries a
  // mailto:support@peachymen.com link. On touch devices the click that
  // follows a tap is delivered AFTER that layout change, so it can land
  // on whatever moved under the finger — opening the mail app right
  // after a successful add (reported in QA 2026-09-09, not reliably
  // reproducible, which is exactly how ghost clicks behave). Swallow
  // protocol-navigation clicks (mailto:/tel:) for a short window after
  // any add-to-cart tap. Nothing else is blocked, and a deliberate tap
  // on the link a moment later still works.
  var addTapAt = 0;
  document.addEventListener('click', function (evt) {
    var t = evt.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-checkout]')) { addTapAt = Date.now(); return; }
    var link = t.closest('a[href^="mailto:"], a[href^="tel:"]');
    if (!link) return;
    if (Date.now() - addTapAt > 800) return;
    evt.preventDefault();
    evt.stopPropagation();
    if (typeof evt.stopImmediatePropagation === 'function') evt.stopImmediatePropagation();
  }, true);

  // ——— The drawer must never open showing a pre-discount price ———
  // The drawer paints from the sections payload its caller hands to
  // renderContents(), and that HTML is built by the ADD request — so a
  // code applied afterwards always arrives too late and the customer
  // watches the price change (Harry, QA 2026-09-09). Everything that
  // opens the drawer goes through renderContents, so gate it centrally
  // here: if the payload's own markup says this cart has a
  // subscription but carries no discount row, apply the code and
  // re-render from THAT response before painting. One request, and the
  // drawer opens correct the first time. Carts with no subscription,
  // and payloads that already show the row, pass straight through.
  function payloadNeedsClean10(parsedState) {
    if (clean10Hopeless) return false;
    var sections = parsedState && parsedState.sections;
    var html = sections && sections['cart-drawer'];
    if (typeof html !== 'string' || !html) return false;
    // The Function-backed CLEAN10 is line-level, so the drawer Liquid
    // stamps data-clean10-applied whenever it sees the code's line
    // allocations — the reliable "already applied" signal (the old
    // cart-level discounts__discount check went blind on 2026-09-09's
    // Function rebuild and cost a probing round trip per first add).
    if (html.indexOf('data-clean10-applied') !== -1) return false;
    return html.indexOf('cart-items-redesign__item--subscription-active') !== -1;
  }

  var clean10Hopeless = false;
  var lastRepaintSig = null;
  // Mirrors the drawer's own _renderToken: a gated render that resolves
  // after a newer render must not repaint the older cart (review
  // 2026-09-09).
  var gateToken = 0;
  function gateDrawerRender(proto) {
    if (!proto || proto.__peachyClean10Gated) return;
    proto.__peachyClean10Gated = true;
    var orig = proto.renderContents;
    proto.renderContents = function (parsedState, openDrawer) {
      var self = this;
      var args = arguments;
      gateToken += 1;
      var myToken = gateToken;
      if (!payloadNeedsClean10(parsedState)) {
        return orig.apply(self, args);
      }
      // Keep any code the shopper already has (2026-10-16 review): a
      // bare `discount: CLEAN10` replaces every code on the cart, so a
      // shopper's own code was silently swapped out. Read the current
      // codes first and send them alongside CLEAN10, like preAddWrite.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return {}; })
        .then(function (cur) {
          var codes = ((cur && cur.discount_codes) || []).map(function (d) {
            return typeof d === 'string' ? d : (d && d.code) || '';
          }).filter(function (c) { return c && c.toUpperCase() !== CLEAN10; });
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              discount: codes.concat(CLEAN10).join(','),
              attributes: { _peachyClean10Auto: '1' },
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname
            })
          });
        })
        .then(function (r) { return r.json(); })
        .then(function (updated) {
          if (myToken !== gateToken) return; // superseded by a newer render
          if (!updated || !updated.sections) throw new Error('no sections');
          if (payloadNeedsClean10(updated)) {
            // Code did not take (expired, disabled, not applicable).
            // Stop gating for this page load rather than delaying every
            // future open by a round trip that never helps.
            clean10Hopeless = true;
          }
          lastKnownItems = updated.items || lastKnownItems;
          orig.call(self, updated, openDrawer);
        })
        .catch(function () {
          // Never trap the drawer: paint what we had — unless a newer
          // render already took over.
          if (myToken === gateToken) orig.call(self, parsedState, openDrawer);
        });
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(function () {
      var ctor = customElements.get('cart-drawer');
      if (ctor) gateDrawerRender(ctor.prototype);
    });
  }

  // ——— Ownerless-gift rescue (2026-09-10) ———
  // The fallback gift sweep stands down on buy-box pages because a
  // rendered gift config means "a buy box owns gifts here". But a box
  // whose JS DIED still renders its config — leaving nobody to sweep
  // (QA found gifts stranded at full price after emptying a cart).
  // Rescue rule, additive and deliberately conservative: if no buy box
  // has made a LIVE ownership claim with the engine, and the page has
  // been up longer than the boot grace period, run the same
  // reconcileFallback that already guards every non-buy-box page.
  // The engine itself still no-ops if an owner claims mid-flight, its
  // page-wide lock serializes passes, and the pass is idempotent — so
  // a healthy page sees no behavior change.
  var GIFT_RESCUE_T0 = Date.now();
  function maybeGiftRescue(cart) {
    var eng = window.PeachyGiftEngine;
    if (!eng || typeof eng.reconcileFallback !== 'function') return;
    if (typeof eng.hasPrimary === 'function' && eng.hasPrimary()) return;
    if (buyBoxOwnsGifts() && Date.now() - GIFT_RESCUE_T0 < 8000) return;
    eng.reconcileFallback(cart);
  }

  function reconcileClean10() {
    if (clean10InFlight) { clean10Pending = true; return; }
    clean10InFlight = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        lastKnownItems = items;
        var hasSub = false;
        items.forEach(function (it) { if (it.selling_plan_allocation) hasSub = true; });
        // Piggyback the ownerless-gift rescue on this fresh cart read —
        // zero extra requests, same debounce.
        maybeGiftRescue(cart);
        var codes = ((cart && cart.discount_codes) || []).map(function (d) {
          return typeof d === 'string' ? d : (d && d.code) || '';
        }).filter(Boolean);
        var hasCode = codes.some(function (c) { return c.toUpperCase() === CLEAN10; });

        var next = null;
        var stripping = false;
        var adopting = false;
        if (hasSub && !hasCode) {
          next = codes.concat(CLEAN10);
        } else if (hasSub && hasCode && !clean10WasAuto(cart)) {
          // ADOPTION: the code is on a subscription cart but unmarked —
          // attached before the marker existed, or synced back from
          // checkout (which never carries it). On a SUB cart the code
          // is ours by policy either way, so claim it; without this,
          // such carts could never be stripped after the sub was
          // removed (QA repro 2026-09-09, long-lived carts). One-time
          // carts are never adopted, so email/popup codes stay safe.
          next = codes;
          adopting = true;
        } else if (!hasSub && hasCode && clean10WasAuto(cart) &&
                   Date.now() >= preAddHoldUntil) {
          next = codes.filter(function (c) { return c.toUpperCase() !== CLEAN10; });
          // Marker is cleared AFTER the write succeeds — clearing first
          // stranded the code on the cart whenever the POST failed.
          stripping = true;
          // Shopify ALSO holds the code in a browser cookie (set by
          // /discount/ links and by checkout itself), and checkout
          // re-applies from it even after the cart code is stripped —
          // QA repro 2026-09-09: sub+OTP cart, sub removed, drawer
          // showed full price, checkout resurrected the 10%. Clearing
          // the cookie here only fires on OUR auto-attach unwinding
          // (marker present), so an email-link shopper who never had
          // the auto-attach keeps their cookie, per the product rule.
          clearDiscountCookie();
        }
        var drawerEl = document.querySelector('cart-drawer');
        if (next === null) {
          // Nothing to write — but the DRAWER may still be painted from
          // markup the server rendered BEFORE the code landed. That race
          // is real: the buy box adds with a sections payload and paints
          // it after our write, so the cart carries CLEAN10 while the
          // drawer shows none, and the next pass has nothing to change
          // (verified 2026-09-09). Repaint from the server when the code
          // is on the cart but no discount row is rendered.
          var sig = hasSub + '|' + hasCode + '|' + (cart && cart.item_count) + '|' + (cart && cart.total_price);
          if (hasSub && hasCode && drawerEl &&
              typeof drawerEl.refreshFromServer === 'function' &&
              !drawerEl.querySelector('[data-clean10-applied]') &&
              lastRepaintSig !== sig) {
            // Once per cart state only. A code that is present but
            // yields no discount application (expired, limit reached)
            // never renders a row, and an unguarded repaint here spun
            // forever (review 2026-09-09).
            lastRepaintSig = sig;
            drawerEl.refreshFromServer();
          }
          return null;
        }

        var drawer = drawerEl;
        var canRender = !!(drawer && typeof drawer.renderContents === 'function');
        var body = {
          discount: next.join(','),
          attributes: {}
        };
        // Stamp/clear the marker in the SAME write that changes the code.
        body.attributes[CLEAN10_ATTR] = stripping ? '' : '1';
        if (adopting) {
          // Marker-only write: the code list is unchanged and nothing
          // visible moves, so skip the section render entirely.
          canRender = false;
        }
        if (canRender) {
          // Re-render rides ON the mutating call — never a separate
          // /cart?sections= fetch afterwards (2026-09-07 drawer rule).
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) { return r.json(); })
          .then(function (updated) {
            if (updated && updated.items) lastKnownItems = updated.items;
            if (canRender && updated && updated.sections) {
              // openDrawer=false unless already open: reconcile must
              // never pop the drawer uninvited (e.g. on page load).
              drawer.renderContents(updated, drawer.classList.contains('active'));
            }
          });
      })
      .catch(function () { /* cart unreachable — checkout departure still attaches via /discount/ */ })
      .then(function () {
        clean10InFlight = false;
        if (clean10Pending) { clean10Pending = false; scheduleClean10Reconcile(); }
      });
  }

  function init() {
    persistConfig();
    applyConfig();
    scheduleClean10Reconcile();

    // The retired /cart page redirects here with ?cart=open (see
    // theme.liquid) — honor it by opening the drawer, then strip the
    // param so a refresh doesn't reopen it.
    if (/[?&]cart=open\b/.test(window.location.search)) {
      var params = new URLSearchParams(window.location.search);
      params.delete('cart');
      var qs = params.toString();
      window.history.replaceState(null, '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
      // The customer explicitly asked for their cart, so keep trying
      // until the cart-drawer element has upgraded (slow devices, late
      // script) — up to ~5s — instead of a single-shot timer.
      var openAttempts = 0;
      var openDrawer = function () {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          if (!drawer.classList.contains('is-empty')) drawer.open();
          return;
        }
        if (openAttempts++ < 20) window.setTimeout(openDrawer, 250);
      };
      window.setTimeout(openDrawer, 300);
    }

    // Reconcile gifts once on load too, not just on cart-update events: a
    // customer can land on a non-buy-box page with a stale over-tier gift
    // (left from a prior session) and go straight to checkout. Guarded by
    // buyBoxOwnsGifts() (stands down where the buy box reconciles) and renders with
    // openDrawer=false, so it never opens the drawer / scroll-locks.
    giftReconcileFallback();

    // On buy-box pages the fallback stands down, which used to leave
    // lastKnownItems null — the checkout guard was unarmed until the
    // first cart event. Seed it with one load-time read so the guard
    // watches from the start on every page.
    if (buyBoxOwnsGifts()) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.items) lastKnownItems = cart.items;
          noteCartForShipArm(cart);
        })
        .catch(function () {});
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.cartData && evt.cartData.items) {
          lastKnownItems = evt.cartData.items;
        }
        noteCartForShipArm(evt && evt.cartData);
        // Drawer renders fresh markup on cart updates — re-sync next paint.
        window.requestAnimationFrame(applyConfig);
        // On non-buy-box pages, keep gifts in step with the bag count.
        giftReconcileFallback();
      });
    }

    // ---- Checkout guard (all pages, drawer + cart page) ----
    // Zero-latency in the normal case: only when the LAST KNOWN cart state
    // shows a gift the customer no longer earns do we hold the click, sweep
    // the unearned gift lines against a FRESH cart read, and then re-submit.
    // This closes the edit→checkout race that produced order #271233.
    document.addEventListener(
      'click',
      function (evt) {
        var btn = evt.target.closest('button[name="checkout"]');
        if (!btn) return;

        // ALWAYS intercept, and NEVER submit the cart form. The drawer's
        // form posts its updates[] quantity inputs on submit, so a stale
        // drawer DOM (background reconcile repaint, a second tab, an app
        // widget writing the cart) OVERWRITES the server cart's
        // quantities on the way to checkout — verified live 2026-08-19:
        // a stale form resurrected two removed bags at the checkout
        // door. The server cart is authoritative; depart via a plain
        // navigation to /checkout.
        evt.preventDefault();
        evt.stopPropagation();
        if (typeof evt.stopImmediatePropagation === 'function') {
          evt.stopImmediatePropagation();
        }

        // NO cached fast path: lastKnownItems only reflects mutations we
        // were told about — an app widget, webhook, second tab, or any
        // API write between the last cart event and this click is
        // invisible to it (verified live 2026-08-19: a charged gift
        // planted after page load sailed through the old fast path to
        // checkout). Every departure verifies against the LIVE cart;
        // the ~200ms fetch is imperceptible next to the checkout
        // navigation itself, and the 5s failsafe below still guarantees
        // the click can never be trapped.
        btn.disabled = true;

        // Single continuation shared by success, error, AND a hard 5s
        // timeout — the guard must never trap a checkout, including on
        // a hung fetch that neither resolves nor rejects.
        var settled = false;
        function proceedToCheckout() {
          if (settled) return;
          settled = true;
          btn.disabled = false;
          // CLEAN10 rides on every SUBSCRIBE checkout (Buy Box V3
          // plan, 2026-09-09): the /discount/ endpoint sets the
          // discount cookie server-side and forwards to checkout, so
          // the code survives any checkout entry. One-time-only carts
          // depart plain — the code is a subscription price component,
          // not an OTP perk. The code is NOT once-per-customer
          // (flipped 2026-09-09), so repeat subscribers never get
          // refused at the checkout door.
          var attachCode = (lastKnownItems || []).some(function (it) {
            return !!it.selling_plan_allocation;
          });
          window.location.href = attachCode
            ? '/discount/CLEAN10?redirect=%2Fcheckout'
            : '/checkout';
        }
        window.setTimeout(proceedToCheckout, 5000);

        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (settled) return null;
            var items = (cart && cart.items) || [];
            lastKnownItems = items;
            var keys = overTierGiftKeys(items);

            // Market rules (mid-session country switch — see the
            // __peachyMarket bridge in the drawer snippet):
            var mkt = window.__peachyMarket || {};
            // 1. Free-gift lines whose product isn't sellable in this
            //    market (Liquid flags the drawer row — e.g. the Mini
            //    Douche after a US→CA switch) are removed before
            //    departure; checkout would hard-reject them anyway.
            //    Only ever gift lines — paid lines are never touched.
            items.forEach(function (it) {
              if (!(it.properties && it.properties._free_gift)) return;
              if (keys.indexOf(it.key) !== -1) return;
              var row = document.querySelector(
                '[data-line-key="' + it.key + '"][data-peachy-market-unavailable]'
              );
              if (row) keys.push(it.key);
            });
            var removals = null;
            if (keys.length) {
              var updates = {};
              keys.forEach(function (k) { updates[k] = 0; });
              removals = fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates: updates })
              }).then(function (r) { return r.json(); })
                .then(function (c) { if (c && c.items) lastKnownItems = c.items; });
            }

            // 2. Pouch cap (Canada import limit, maxBags 3): a cart
            //    built in the US market can exceed it. This is a
            //    DELIBERATE stop, not a trapped click — the shopper
            //    keeps a working page with the drawer open and the
            //    warning explaining exactly what to remove. The 5s
            //    failsafe stays armed for fetch failures only.
            if (mkt.maxBags > 0 && countCapPouches(items) > mkt.maxBags) {
              settled = true;
              btn.disabled = false;
              var warn = document.querySelector('[data-peachy-market-warning]');
              if (warn) {
                warn.hidden = false;
                warn.classList.remove('is-attention');
                void warn.offsetWidth;
                warn.classList.add('is-attention');
              }
              var drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.open === 'function') drawer.open();
              return removals; // gift removals still land; no navigation
            }
            return removals;
          })
          .catch(function () { /* proceed regardless — never trap a checkout */ })
          .then(proceedToCheckout);
      },
      true
    );

    // The cart-drawer custom element also calls renderContents directly
    // for some flows (initial load with items, certain refresh paths).
    // Watch for inner DOM swaps so we re-apply even when no cartUpdate
    // event fires.
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      var observer = new MutationObserver(function () {
        window.requestAnimationFrame(applyConfig);
        // Every cart write repaints the drawer, so this doubles as the
        // "cart changed" signal for the CLEAN10 attach. Debounced; the
        // reconcile no-ops when the code state already matches, so its
        // own repaint terminates the cycle.
        scheduleClean10Reconcile();
      });
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
